import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Calculator, ShieldCheck, ArrowLeftRight, Banknote, CreditCard, Globe, AlertTriangle, HelpCircle, Upload } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import analytics from "@/utils/analytics";
import { fadeUp, stagger, viewportOnce } from "@/utils/motionVariants";

const iconGridInitial = { opacity: 0, y: 20 };
const iconGridAnimate = { opacity: 1, y: 0 };
const iconGridTransition = { duration: 0.5, delay: 0.2 };

const iconItemInitial = { opacity: 0, scale: 0.9 };
const iconItemAnimate = { opacity: 1, scale: 1 };

const HERO_TOOL = {
  title: "Payment Solution Finder",
  desc: "Not sure where to start? Answer 7 quick questions and we'll recommend the right tool for your business.",
  to: "/payment-finder",
  icon: HelpCircle,
  itemName: "payment_solution_finder",
  itemType: "finder",
};

const TOOL_GROUPS = [
  {
    heading: "Check your costs",
    desc: "Find out if you're overpaying or where your money is going.",
    tools: [
      { title: "Savings Calculator", desc: "Compare your current card processing costs with CanDo pricing.", to: "/switch-and-save", icon: Calculator, itemName: "savings_calculator", itemType: "calculator" },
      { title: "Overpaying Scorecard", desc: "Score your current rates and fees against industry benchmarks.", to: "/overpaying-scorecard", icon: AlertTriangle, itemName: "overpaying_scorecard", itemType: "calculator" },
      { title: "Hidden Fees Checker", desc: "Check if your PCI, terminal, auth, or service fees look high.", to: "/hidden-fees-checker", icon: ShieldCheck, itemName: "hidden_fees_checker", itemType: "calculator" },
      { title: "Cost of Cash Calculator", desc: "See the real cost of handling cash — labour, banking, and losses.", to: "/cost-of-cash", icon: Banknote, itemName: "cost_of_cash_calculator", itemType: "calculator" },
    ],
  },
  {
    heading: "Get a quote",
    desc: "Estimate your likely monthly costs for in-person or online payments.",
    tools: [
      { title: "In-Person Quote", desc: "Estimate costs for card machines, EPOS, and in-person processing.", to: "/quote/in-person", icon: CreditCard, itemName: "in_person_quote", itemType: "quote_tool" },
      { title: "Online Quote", desc: "Estimate costs for online card processing and gateway fees.", to: "/quote/online", icon: Globe, itemName: "online_quote", itemType: "quote_tool" },
    ],
  },
  {
    heading: "Compare options",
    desc: "Weigh up the numbers before making a decision.",
    tools: [
      { title: "Cash vs Card Comparator", desc: "Side-by-side comparison of handling cash versus taking cards.", to: "/cash-vs-card", icon: ArrowLeftRight, itemName: "cash_vs_card_comparator", itemType: "comparison" },
      { title: "Switch vs Stay Calculator", desc: "See if switching provider would pay off and how quickly.", to: "/switch-vs-stay", icon: Zap, itemName: "switch_vs_stay_calculator", itemType: "calculator" },
    ],
  },
  {
    heading: "Request a review",
    desc: "Send us your statement and we'll do the detailed analysis for you.",
    tools: [
      { title: "Statement Review Upload", desc: "Upload your latest processing statement for a tailored, line-by-line review.", to: "/statement-review", icon: Upload, itemName: "statement_review_upload", itemType: "upload" },
    ],
  },
];

function ToolCard({ tool }) {
  const Icon = tool.icon;
  const onClick = () => analytics.toolOpened(tool.itemName, { itemType: tool.itemType, source: "/tools" });
  return (
    <Link to={tool.to} onClick={onClick} className="group bg-white border border-brand-light-grey rounded-2xl p-6 hover:border-brand-aqua hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col" data-testid={`tool-card-${tool.to.replace(/\//g, "-").slice(1)}`} data-measurement-owned="tool">
      <div className="w-10 h-10 rounded-xl bg-brand-aqua-light flex items-center justify-center mb-4 group-hover:bg-brand-aqua transition-colors">
        <Icon size={20} className="text-brand-aqua group-hover:text-white transition-colors" />
      </div>
      <h3 className="font-heading text-base font-bold text-brand-black mb-2">{tool.title}</h3>
      <p className="text-sm text-brand-grey leading-relaxed flex-1">{tool.desc}</p>
      <span className="mt-4 text-sm font-semibold text-brand-aqua flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
        Open free <ArrowRight size={14} />
      </span>
    </Link>
  );
}

export default function ToolsPage() {
  return (
    <div>
      <SEOHead title="Payment Tools for Business" description="Streamline your business operations with our comprehensive suite of payment tools. From savings calculator to hidden fee checkers, we have it all!" path="/tools" />

      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">Free Tools</motion.p>
              <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-6">Payment tools that help you <span className="gradient-text">make better decisions</span></motion.h1>
              <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed">Free calculators, scorecards, and comparison tools — built to help you understand your costs, explore your options, and find the right payment setup for your business.</motion.p>
            </motion.div>
            <motion.div initial={iconGridInitial} animate={iconGridAnimate} transition={iconGridTransition} className="hidden lg:grid grid-cols-3 gap-3 max-w-xs mx-auto">
              {[
                { icon: Calculator, label: "Savings" },
                { icon: AlertTriangle, label: "Scorecard" },
                { icon: ShieldCheck, label: "Fee Check" },
                { icon: CreditCard, label: "In-Person" },
                { icon: Globe, label: "Online" },
                { icon: ArrowLeftRight, label: "Compare" },
              ].map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={iconItemInitial}
                  animate={iconItemAnimate}
                  transition={{ delay: 0.4 + i * 0.08 }}
                  className="bg-white border border-brand-light-grey/60 rounded-xl p-3 text-center shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                >
                  <item.icon size={20} className="text-brand-aqua mx-auto mb-1" />
                  <p className="text-[10px] font-bold text-brand-grey">{item.label}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={fadeUp}>
            <Link to={HERO_TOOL.to} onClick={() => analytics.toolOpened(HERO_TOOL.itemName, { itemType: HERO_TOOL.itemType, source: "/tools" })} className="group block bg-brand-aqua-light border-2 border-brand-aqua/20 rounded-2xl p-6 sm:p-8 hover:border-brand-aqua hover:shadow-lg transition-all duration-300" data-testid="tool-hero-finder" data-measurement-owned="tool">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
                <div className="w-14 h-14 rounded-2xl bg-brand-aqua flex items-center justify-center flex-shrink-0">
                  <HERO_TOOL.icon size={26} className="text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-brand-aqua mb-1">Not sure where to start?</p>
                  <h2 className="font-heading text-xl sm:text-2xl font-bold text-brand-black mb-1">{HERO_TOOL.title}</h2>
                  <p className="text-sm text-brand-grey leading-relaxed">{HERO_TOOL.desc}</p>
                </div>
                <span className="bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-6 py-3 text-sm flex items-center gap-2 transition-colors btn-shine flex-shrink-0">
                  Find my best option (60 sec) <ArrowRight size={16} />
                </span>
              </div>
            </Link>
          </motion.div>
        </div>
      </section>

      {TOOL_GROUPS.map((group) => {
        let gridCols;
        if (group.tools.length === 1) gridCols = "sm:grid-cols-1 max-w-lg";
        else if (group.tools.length === 2) gridCols = "sm:grid-cols-2";
        else gridCols = "sm:grid-cols-2 lg:grid-cols-4";
        return (
          <section key={group.heading} className="py-12 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger}>
                <motion.div variants={fadeUp} className="mb-6">
                  <h2 className="font-heading text-2xl font-bold text-brand-black tracking-tight">{group.heading}</h2>
                  <p className="text-sm text-brand-grey mt-1">{group.desc}</p>
                </motion.div>
                <motion.div variants={stagger} className={`grid grid-cols-1 ${gridCols} gap-5`}>
                  {group.tools.map((tool) => (
                    <motion.div key={tool.to} variants={fadeUp}>
                      <ToolCard tool={tool} />
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            </div>
          </section>
        );
      })}

      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl font-bold text-brand-black mb-3">Prefer to speak to someone?</h2>
          <p className="text-sm text-brand-grey mb-6">Call us on <a href="tel:+448003682989" className="text-brand-aqua font-semibold">0800 368 2989</a> or book a call at a time that suits you.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="/book" className="bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors btn-shine">Book a Call <ArrowRight size={16} /></a>
            <Link to="/contact" className="bg-white border border-brand-aqua text-brand-aqua-dark font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 hover:bg-brand-aqua-light transition-colors">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
